import { useMemo, useState, type MouseEvent } from "react";
import NearAvailabilityIcon from "@mui/icons-material/AccessTime";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DrivingIcon from "@mui/icons-material/DirectionsCar";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EmailIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import SmsIcon from "@mui/icons-material/Sms";
import TransitIcon from "@mui/icons-material/Train";
import WithdrawnIcon from "@mui/icons-material/Undo";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import type { Dayjs, TimeRangeOnDate } from "@thalamos/common";
import {
  dayjs,
  formatClockTime,
  ISO_DATE_FORMAT,
  IsoDateSchema,
  timeRangesOverlap,
  UK_TIME_ZONE,
} from "@thalamos/common";
import type {
  InterviewContactMethod,
  RouteTimeResult,
  Section12DoctorSearchResult,
  Section12RequestUserStatus,
} from "@thalamos/aspire-common";
import {
  SECTION_12_EXPERTISE_LEVEL_LABELS,
  SECTION_12_GENDER_LABELS,
} from "@thalamos/aspire-common";

import type { DoctorMatchCriteria } from "../utils/doctorMatching.js";
import { doctorAvailabilityOn } from "../utils/doctorMatching.js";

interface DoctorCardProps {
  doctor: Section12DoctorSearchResult;
  isMatching: boolean;
  isSelected: boolean;
  onToggleSelection: (doctorId: string, selected: boolean) => void;
  defaultExpanded?: boolean;
  existingStatus?: Section12RequestUserStatus;
  matchedSpecialismCount?: number; // Number of specialisms matching the filter criteria
  /** Free for long enough to be worth asking, but not for a whole assessment. */
  hasNearAvailability?: boolean;
  /** The stretch that fell short, so the badge can name it. */
  nearAvailabilityStretch?: TimeRangeOnDate;
  matchesGender?: boolean; // Whether the doctor matches the gender filter criteria
  matchesLanguages?: boolean; // Whether the doctor matches the language filter criteria
  matchedLanguageCount?: number; // Number of languages matching the filter criteria
  /** The filter the card is read against: which blocks it shows, and which it marks. */
  criteria?: DoctorMatchCriteria;
  routeTime?: RouteTimeResult;
  routeTimeLoading?: boolean;
  /** UK wall-clock now, from which the upcoming-availability chips are read. */
  now?: Dayjs;
  onContactDoctor?: (
    doctorId: string,
    method: InterviewContactMethod[],
  ) => void;
  isContacting?: boolean;
}

const getStatusConfig = (status: Section12RequestUserStatus) => {
  switch (status) {
    case "pending":
      return {
        label: "Contacted",
        color: "warning" as const,
        icon: undefined,
      };
    case "accepted":
      return {
        label: "Accepted",
        color: "success" as const,
        icon: <CheckCircleIcon sx={{ fontSize: "0.875rem" }} />,
      };
    case "withdrawn":
      return {
        label: "Withdrawn",
        color: "default" as const,
        icon: <WithdrawnIcon sx={{ fontSize: "0.875rem" }} />,
      };
    case "rejected":
      return {
        label: "Declined",
        color: "error" as const,
        icon: undefined,
      };
    default:
      return null;
  }
};

/** How far ahead the availability chips look. */
const UPCOMING_DAYS = 14;
const DAYS_PER_WEEK = 7;

/**
 * A free stretch as `"Free 13:00-13:45 on Tue 8 Sep"`. Parsed in UTC so the
 * zoneless date is not reinterpreted in the browser's zone.
 */
function formatFreeStretch(stretch: TimeRangeOnDate): string {
  const day = dayjs.utc(stretch.date, ISO_DATE_FORMAT).format("ddd D MMM");
  return `Free ${formatClockTime(stretch.from)}-${formatClockTime(stretch.until)} on ${day}`;
}

/** One of the doctor's free blocks on one of the days ahead. */
interface UpcomingBlock {
  key: string;
  dayOffset: number;
  label: string;
  /** Overlaps time that is being asked for, so it answers the search. */
  matched: boolean;
}

export function DoctorCard({
  doctor,
  isMatching,
  isSelected,
  onToggleSelection,
  defaultExpanded = false,
  existingStatus,
  matchedSpecialismCount,
  hasNearAvailability,
  nearAvailabilityStretch,
  matchesGender,
  matchedLanguageCount,
  criteria,
  routeTime,
  routeTimeLoading,
  now,
  onContactDoctor,
  isContacting,
}: DoctorCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [callConfirmOpen, setCallConfirmOpen] = useState(false);

  // UK wall clock, the zone every availability date is read in.
  const cardNow = useMemo(() => now ?? dayjs().tz(UK_TIME_ZONE), [now]);

  const requestedRanges = useMemo(
    () => criteria?.requestedRanges ?? [],
    [criteria],
  );
  const hasAvailabilityFilter = requestedRanges.length > 0;

  const upcomingBlocks = useMemo(() => {
    const blocks: UpcomingBlock[] = [];
    for (let dayOffset = 0; dayOffset < UPCOMING_DAYS; dayOffset++) {
      const date = IsoDateSchema.parse(
        cardNow.add(dayOffset, "day").format(ISO_DATE_FORMAT),
      );
      const rangesOnDate = requestedRanges.filter(
        (range) => range.date === date,
      );
      for (const block of doctorAvailabilityOn(doctor, date)) {
        blocks.push({
          key: `${date} ${String(block.from)}`,
          dayOffset,
          label: `${dayjs.utc(date, ISO_DATE_FORMAT).format("ddd D")}: ${formatClockTime(block.from)}-${formatClockTime(block.until)}`,
          matched: rangesOnDate.some((range) =>
            timeRangesOverlap([block], range),
          ),
        });
      }
    }
    return blocks;
  }, [doctor, requestedRanges, cardNow]);

  // With a filter on, a block that answers nothing the AMHP asked for is noise.
  const shownBlocks = hasAvailabilityFilter
    ? upcomingBlocks.filter((block) => block.matched)
    : upcomingBlocks;
  const firstWeekBlocks = shownBlocks.filter(
    (block) => block.dayOffset < DAYS_PER_WEEK,
  );
  const secondWeekBlocks = shownBlocks.filter(
    (block) => block.dayOffset >= DAYS_PER_WEEK,
  );

  const statusConfig = existingStatus ? getStatusConfig(existingStatus) : null;
  const isSelectionLocked =
    existingStatus === "pending" || existingStatus === "accepted";
  // Contact chips (EMAIL/SMS) are locked only for doctors who have accepted or
  // confirmed — AMHPs can re-contact doctors who are still in "Contacted" (pending).
  const isContactLocked = existingStatus === "accepted";
  const isContactDisabled = Boolean(isContactLocked || isContacting);

  // Outlined style for all contact chips — matches Aspire outlined button behaviour:
  // transparent bg with a primary-coloured border; hover fills to primary + white text.
  const contactChipSx = {
    height: 28,
    borderRadius: "4px",
    bgcolor: "transparent",
    color: isContactDisabled ? "text.disabled" : "primary.main",
    border: "1px solid",
    borderColor: isContactDisabled ? "text.disabled" : "primary.main",
    "& .MuiChip-icon": {
      color: isContactDisabled ? "text.disabled" : "primary.main",
    },
    "& .MuiChip-label": {
      px: 1,
      fontSize: "0.75rem",
      fontWeight: 600,
    },
    "&.Mui-disabled": { opacity: 1 },
    ...(!isContactDisabled && {
      "&:hover": {
        bgcolor: "primary.main",
        borderColor: "primary.main",
        color: "common.white",
        "& .MuiChip-icon": { color: "common.white" },
      },
    }),
  };

  // Renders the contact chips for both the inline header row and the mobile-only second row.
  const renderContactChips = (testIdSuffix: string) => (
    <>
      {doctor.phoneNumber && (
        <Chip
          icon={<PhoneIcon sx={{ fontSize: "0.875rem" }} />}
          label="CALL"
          size="small"
          clickable={!isContactLocked}
          disabled={isContacting || isContactLocked}
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            if (!isContactLocked) {
              setCallConfirmOpen(true);
              const a = document.createElement("a");
              a.href = `tel:${doctor.phoneNumber}`;
              a.click();
            }
          }}
          sx={contactChipSx}
          data-testid={`doctor-call-chip-${testIdSuffix}`}
        />
      )}
      {doctor.phoneNumber && (
        <Chip
          icon={
            isContacting ? (
              <CircularProgress size={12} />
            ) : (
              <SmsIcon sx={{ fontSize: "0.875rem" }} />
            )
          }
          label="SMS"
          size="small"
          clickable={!isContactLocked}
          disabled={isContacting || isContactLocked}
          onClick={(e) => {
            e.stopPropagation();
            if (!isContactLocked) {
              onContactDoctor?.(doctor.id, ["sms"]);
            }
          }}
          sx={contactChipSx}
          data-testid={`doctor-sms-chip-${testIdSuffix}`}
        />
      )}
      <Chip
        icon={
          isContacting ? (
            <CircularProgress size={12} />
          ) : (
            <EmailIcon sx={{ fontSize: "0.875rem" }} />
          )
        }
        label="EMAIL"
        size="small"
        clickable={!isContactLocked}
        disabled={isContacting || isContactLocked}
        onClick={(e) => {
          e.stopPropagation();
          if (!isContactLocked) {
            onContactDoctor?.(doctor.id, ["email"]);
          }
        }}
        sx={contactChipSx}
        data-testid={`doctor-email-chip-${testIdSuffix}`}
      />
    </>
  );

  return (
    <Paper
      elevation={1}
      sx={{
        border: isMatching
          ? `2px solid ${theme.palette.success.main}`
          : `1px solid ${theme.palette.divider}`,
        backgroundColor: isMatching
          ? theme.palette.success.light + "20"
          : theme.palette.background.paper,
        borderRadius: 2,
        p: 1.5,
        mb: 1,
        maxWidth: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: theme.shadows[3],
        },
      }}
      data-testid={`doctor-card-${doctor.id}`}
    >
      {/* Collapsed header - always visible */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {/* Main header row: checkbox + name/badges + contact chips (desktop) + expand button */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isSelected}
                disabled={isSelectionLocked}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleSelection(doctor.id, e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                size="small"
                data-testid={`doctor-checkbox-${doctor.id}`}
              />
            }
            label=""
            sx={{ m: 0 }}
          />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              cursor: "pointer",
              minWidth: 0,
              overflow: "hidden",
            }}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
                maxWidth: "100%",
              }}
            >
              {doctor.name}
            </Typography>
            {isMatching && (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: "0.875rem" }} />}
                label="Match"
                size="small"
                sx={{
                  height: 20,
                  borderRadius: "4px",
                  bgcolor: "chip.matchBackground",
                  color: "chip.matchText",
                  "& .MuiChip-icon": { color: "chip.matchText" },
                  "& .MuiChip-label": {
                    px: 0.75,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                  },
                }}
                data-testid={`doctor-matching-badge-${doctor.id}`}
              />
            )}
            {/* Show near availability badge for non-matching doctors */}
            {!isMatching && hasNearAvailability && (
              <Tooltip
                title={
                  nearAvailabilityStretch === undefined
                    ? "Free for part of the time you asked for, but not long enough for a whole assessment"
                    : formatFreeStretch(nearAvailabilityStretch)
                }
              >
                <Chip
                  icon={<NearAvailabilityIcon sx={{ fontSize: "0.875rem" }} />}
                  label="Near availability"
                  color="info"
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    "& .MuiChip-label": { px: 0.75, fontSize: "0.7rem" },
                  }}
                  data-testid={`doctor-near-availability-badge-${doctor.id}`}
                />
              </Tooltip>
            )}
            {/* Show matched specialism count in collapsed view (only when filter is active) */}
            {!isExpanded &&
              !isMatching &&
              matchedSpecialismCount !== undefined &&
              matchedSpecialismCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  • {String(matchedSpecialismCount)} specialism
                  {matchedSpecialismCount !== 1 ? "s" : ""} matched
                </Typography>
              )}
            {/* Show gender match indicator in collapsed view (only when gender filter is active) */}
            {!isExpanded &&
              !isMatching &&
              matchesGender &&
              criteria?.genderValues &&
              criteria.genderValues.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  • Gender matched
                </Typography>
              )}
            {/* Show language match indicator in collapsed view (only when language filter is active) */}
            {!isExpanded &&
              !isMatching &&
              matchedLanguageCount !== undefined &&
              matchedLanguageCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  • {String(matchedLanguageCount)} language
                  {matchedLanguageCount !== 1 ? "s" : ""} matched
                </Typography>
              )}
            {/* Show existing status badge — last in chip order */}
            {statusConfig && (
              <Chip
                icon={statusConfig.icon}
                label={statusConfig.label}
                color={statusConfig.color}
                size="small"
                variant={existingStatus === "pending" ? "filled" : "outlined"}
                sx={{
                  height: 20,
                  "& .MuiChip-label": { px: 0.75, fontSize: "0.7rem" },
                  ...(existingStatus === "pending" && {
                    bgcolor: "warning.main",
                    color: "text.primary",
                    "& .MuiChip-icon": { color: "text.primary" },
                  }),
                }}
                data-testid={`doctor-status-badge-${doctor.id}`}
              />
            )}
            {/* Show travel time chips in collapsed view on desktop */}
            {!isExpanded && !routeTimeLoading && routeTime && (
              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                  gap: 0.5,
                  alignItems: "center",
                  ml: "auto",
                  flexShrink: 0,
                }}
              >
                {routeTime.drivingTimeMinutes != null && (
                  <Chip
                    icon={<DrivingIcon sx={{ fontSize: "0.75rem" }} />}
                    label={`${String(routeTime.drivingTimeMinutes)} min`}
                    size="small"
                    variant="outlined"
                    color="info"
                    sx={{
                      height: 20,
                      "& .MuiChip-label": { px: 0.5, fontSize: "0.65rem" },
                      "& .MuiChip-icon": { ml: 0.5 },
                    }}
                  />
                )}
                {routeTime.transitTimeMinutes != null && (
                  <Chip
                    icon={<TransitIcon sx={{ fontSize: "0.75rem" }} />}
                    label={`${String(routeTime.transitTimeMinutes)} min`}
                    size="small"
                    variant="outlined"
                    color="info"
                    sx={{
                      height: 20,
                      "& .MuiChip-label": { px: 0.5, fontSize: "0.65rem" },
                      "& .MuiChip-icon": { ml: 0.5 },
                    }}
                  />
                )}
              </Box>
            )}
            {/* Show loading skeletons in collapsed view on desktop */}
            {!isExpanded && routeTimeLoading && (
              <Box
                data-testid={`doctor-route-time-loading-${doctor.id}`}
                sx={{
                  display: { xs: "none", sm: "flex" },
                  gap: 0.5,
                  alignItems: "center",
                  ml: "auto",
                  flexShrink: 0,
                }}
              >
                <Skeleton
                  variant="rounded"
                  width={70}
                  height={20}
                  sx={{ borderRadius: 2 }}
                />
                <Skeleton
                  variant="rounded"
                  width={70}
                  height={20}
                  sx={{ borderRadius: 2 }}
                />
              </Box>
            )}
          </Box>

          {/* Contact action chips — desktop: inline in the header row */}
          {onContactDoctor && (
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                gap: 1,
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              {renderContactChips(doctor.id)}
            </Box>
          )}

          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? "Collapse details" : "Expand details"}
            data-testid={`doctor-expand-button-${doctor.id}`}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Contact action chips — mobile: own row below the name row */}
        {onContactDoctor && (
          <Box
            sx={{
              display: { xs: "flex", sm: "none" },
              gap: 1,
              alignItems: "center",
              pl: 4.5,
            }}
          >
            {renderContactChips(`${doctor.id}-mobile`)}
          </Box>
        )}
      </Box>
      {/* Expanded details */}
      <Collapse in={isExpanded}>
        <Box sx={{ pl: 5, pt: 1.5 }}>
          {/* Contact row: Email and Phone */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {doctor.email}
              </Typography>
            </Box>
            {doctor.phoneNumber ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneIcon fontSize="small" color="action" />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {doctor.phoneNumber}
                </Typography>
                <Chip
                  icon={<SmsIcon sx={{ fontSize: "0.875rem" }} />}
                  label="SMS enabled"
                  color="success"
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    "& .MuiChip-label": { px: 0.75, fontSize: "0.7rem" },
                  }}
                  data-testid={`doctor-sms-enabled-${doctor.id}`}
                />
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Email only
              </Typography>
            )}
          </Box>

          {/* Gender */}
          {doctor.gender && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Gender: {SECTION_12_GENDER_LABELS[doctor.gender]}
              </Typography>
            </Box>
          )}

          {/* Trust Information */}
          {(doctor.worksForTrust === true ||
            (doctor.worksForTrust === undefined &&
              (doctor.trustName || doctor.trustTeam))) && (
            <Box sx={{ mb: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <BusinessIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Trust Affiliation
                </Typography>
              </Box>
              {doctor.trustName && (
                <Typography variant="body2" color="text.secondary">
                  {doctor.trustName}
                </Typography>
              )}
              {doctor.trustTeam && (
                <Typography variant="body2" color="text.secondary">
                  Team: {doctor.trustTeam}
                </Typography>
              )}
            </Box>
          )}

          {/* Languages */}
          {doctor.languages.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  mb: 0.5,
                }}
              >
                Languages
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {doctor.languages.map((lang) => {
                  const isLanguageMatched =
                    criteria?.languageIds?.includes(lang.id) ?? false;
                  return (
                    <Chip
                      key={lang.id}
                      label={lang.languageName}
                      size="small"
                      variant={isLanguageMatched ? "filled" : "outlined"}
                      color={isLanguageMatched ? "success" : "default"}
                      sx={{
                        height: 22,
                        fontSize: "0.75rem",
                        ...(isLanguageMatched && {
                          backgroundColor: theme.palette.success.light + "40",
                          borderColor: theme.palette.success.main,
                        }),
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Route Times */}
          {(routeTime || routeTimeLoading) && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {routeTimeLoading && (
                  <>
                    <Skeleton
                      variant="rounded"
                      width={100}
                      height={22}
                      sx={{ borderRadius: 2 }}
                    />
                    <Skeleton
                      variant="rounded"
                      width={100}
                      height={22}
                      sx={{ borderRadius: 2 }}
                    />
                  </>
                )}
                {!routeTimeLoading && routeTime?.drivingTimeMinutes != null && (
                  <Chip
                    icon={<DrivingIcon sx={{ fontSize: "0.875rem" }} />}
                    label={`${String(routeTime.drivingTimeMinutes)} min drive`}
                    size="small"
                    variant="outlined"
                    color="info"
                    sx={{
                      height: 22,
                      "& .MuiChip-label": { px: 0.75, fontSize: "0.7rem" },
                    }}
                    data-testid={`doctor-driving-time-${doctor.id}`}
                  />
                )}
                {!routeTimeLoading && routeTime?.transitTimeMinutes != null && (
                  <Chip
                    icon={<TransitIcon sx={{ fontSize: "0.875rem" }} />}
                    label={`${String(routeTime.transitTimeMinutes)} min transit`}
                    size="small"
                    variant="outlined"
                    color="info"
                    sx={{
                      height: 22,
                      "& .MuiChip-label": { px: 0.75, fontSize: "0.7rem" },
                    }}
                    data-testid={`doctor-transit-time-${doctor.id}`}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Specialisms */}
          {doctor.specialisms.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  mb: 0.5,
                }}
              >
                Specialisms
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {doctor.specialisms.map((spec) => {
                  const isSpecialismMatched =
                    criteria?.specialismIds?.includes(spec.id) ?? false;
                  const expertiseLabel = spec.expertiseLevel
                    ? SECTION_12_EXPERTISE_LEVEL_LABELS[spec.expertiseLevel]
                    : null;
                  const chipLabel = expertiseLabel
                    ? `${spec.specialismName} (${expertiseLabel})`
                    : spec.specialismName;
                  return (
                    <Chip
                      key={spec.id}
                      label={chipLabel}
                      size="small"
                      variant={isSpecialismMatched ? "filled" : "outlined"}
                      color={isSpecialismMatched ? "success" : "default"}
                      sx={{
                        height: 22,
                        fontSize: "0.75rem",
                        ...(isSpecialismMatched && {
                          backgroundColor: theme.palette.success.light + "40",
                          borderColor: theme.palette.success.main,
                        }),
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Local Authorities */}
          {doctor.localAuthorities.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  mb: 0.5,
                }}
              >
                Local Authorities
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {doctor.localAuthorities.map((la) => (
                  <Chip
                    key={la.locationId}
                    label={la.locationName}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ height: 22, fontSize: "0.75rem" }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.5,
              }}
            >
              <CalendarIcon fontSize="small" color="action" />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                Upcoming Availability
              </Typography>
            </Box>
            {shownBlocks.length === 0 ? (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontStyle: "italic",
                }}
              >
                {hasAvailabilityFilter
                  ? "No matching availability in the next 2 weeks"
                  : "No availability in the next 2 weeks"}
              </Typography>
            ) : (
              <>
                {firstWeekBlocks.length > 0 && (
                  <Box sx={{ mb: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        fontSize: "0.65rem",
                      }}
                    >
                      {cardNow.format("D MMM")} -{" "}
                      {cardNow.add(6, "day").format("D MMM")}:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mt: 0.25,
                      }}
                    >
                      {firstWeekBlocks.map((block) => (
                        <Chip
                          key={block.key}
                          label={block.label}
                          size="small"
                          variant={block.matched ? "filled" : "outlined"}
                          color={block.matched ? "success" : "default"}
                          sx={{
                            fontSize: "0.65rem",
                            height: 20,
                            ...(block.matched && {
                              backgroundColor:
                                theme.palette.success.light + "40",
                              borderColor: theme.palette.success.main,
                            }),
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                {secondWeekBlocks.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        fontSize: "0.65rem",
                      }}
                    >
                      {cardNow.add(7, "day").format("D MMM")} -{" "}
                      {cardNow.add(13, "day").format("D MMM")}:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mt: 0.25,
                      }}
                    >
                      {secondWeekBlocks.map((block) => (
                        <Chip
                          key={block.key}
                          label={block.label}
                          size="small"
                          variant={block.matched ? "filled" : "outlined"}
                          color={block.matched ? "success" : "default"}
                          sx={{
                            fontSize: "0.65rem",
                            height: 20,
                            ...(block.matched && {
                              backgroundColor:
                                theme.palette.success.light + "40",
                              borderColor: theme.palette.success.main,
                            }),
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Collapse>

      {/* CALL confirmation dialog */}
      <Dialog
        open={callConfirmOpen}
        fullScreen={isMobile}
        onClose={() => {
          setCallConfirmOpen(false);
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogTitle>Did you reach this doctor?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Confirming will update {doctor.name}&apos;s status to
            &quot;Contacted&quot;.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setCallConfirmOpen(false);
            }}
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              setCallConfirmOpen(false);
              onContactDoctor?.(doctor.id, ["call"]);
            }}
          >
            Yes, mark as contacted
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
